<template>
  <AIVDialog
    v-model="isVisible"
    title="Review Time Off Request"
    width="500px"
    height="auto"
    class="time-off-review-modal"
  >
    <div v-if="request" class="review-details-content">
      <!-- Request Details -->
      <div class="detail-row">
        <strong>Employee:</strong>
        <span>{{ employeeName }}</span>
      </div>
      <div class="detail-row">
        <strong>Type:</strong>
        <span>{{ formatRequestType(request.request_type) }}</span>
      </div>
      <div class="detail-row">
        <strong>{{
          request.request_type === "multiple_full_days"
            ? "Start Date:"
            : "Date:"
        }}</strong>
        <span>{{ formatDate(request.start_date) }}</span>
      </div>
      <div
        class="detail-row"
        v-if="request.request_type === 'multiple_full_days'"
      >
        <strong>End Date:</strong>
        <span>{{ formatDate(request.end_date) }}</span>
      </div>
      <div
        class="detail-row"
        v-if="request.request_type === 'single_partial_day'"
      >
        <strong>Time:</strong>
        <span>{{ formatTimeRange(request.start_time, request.end_time) }}</span>
      </div>
      <div
        class="detail-row"
        v-if="request.request_type !== 'single_partial_day'"
      >
        <strong>Duration:</strong>
        <span>{{ numberOfDays }} day{{ numberOfDays > 1 ? "s" : "" }}</span>
      </div>
      <div class="detail-row">
        <strong>Status:</strong>
        <el-tag :type="getStatusType(request.status)" size="small">
          {{ capitalizeStatus(request.status) }}
        </el-tag>
      </div>
      <div class="detail-row">
        <strong>Reason:</strong>
        <span>{{ request.reason || "N/A" }}</span>
      </div>

      <!-- Affected Shifts Warning -->
      <div v-if="affectedShifts.length > 0" class="affected-shifts-warning">
        <div class="alert alert-warning">
          <div class="warning-header">
            <i class="fa fa-exclamation-triangle"></i>
            <strong
              >{{ affectedShifts.length }} Shift{{
                affectedShifts.length > 1 ? "s" : ""
              }}
              Will Be Unassigned</strong
            >
          </div>
          <div class="shifts-list">
            <div
              v-for="shift in affectedShifts.slice(0, 3)"
              :key="shift.id"
              class="shift-item"
            >
              {{ formatShiftDate(shift.scheduled_start) }}
              <span v-if="shift.clients && shift.clients.length">
                - {{ shift.clients[0].first_name }}
                {{ shift.clients[0].last_name }}
              </span>
            </div>
            <div v-if="affectedShifts.length > 3" class="text-muted">
              ... and {{ affectedShifts.length - 3 }} more
            </div>
          </div>
        </div>
      </div>

      <!-- No Shifts Info -->
      <div v-else-if="shiftsChecked" class="no-shifts-info">
        <div class="alert alert-info">
          <i class="fa fa-info-circle"></i>
          No scheduled shifts during this time period
        </div>
      </div>

      <!-- Admin Notes -->
      <div class="admin-notes-section">
        <label><strong>Admin Notes (Optional):</strong></label>
        <textarea
          v-model="adminNotes"
          class="form-control"
          rows="3"
          placeholder="Add any notes about your decision..."
        ></textarea>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <button
          @click="handleApprove"
          :disabled="isProcessing"
          class="btn btn-success"
        >
          <i class="fa fa-check"></i>
          {{ isProcessing ? "Processing..." : "Approve" }}
        </button>
        <button
          @click="handleDeny"
          :disabled="isProcessing"
          class="btn btn-danger ml-2"
        >
          <i class="fa fa-times"></i>
          {{ isProcessing ? "Processing..." : "Deny" }}
        </button>
        <button
          @click="closeModal"
          class="btn btn-secondary ml-2"
          :disabled="isProcessing"
        >
          Cancel
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { timeOffRequestsService } from "src/serviceHandlers/timeOffRequestsService";
import { shiftsService } from "src/serviceHandlers/shiftsService";

export default {
  name: "TimeOffReviewModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      adminNotes: "",
      affectedShifts: [],
      shiftsChecked: false,
      isProcessing: false,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    employeeName() {
      if (!this.request || !this.request.caregiver) return "N/A";
      return `${this.request.caregiver.first_name} ${this.request.caregiver.last_name}`;
    },
    numberOfDays() {
      if (!this.request) return 0;
      // Parse dates as local dates to avoid timezone issues
      const [startYear, startMonth, startDay] = this.request.start_date
        .split("T")[0]
        .split("-");
      const [endYear, endMonth, endDay] = this.request.end_date
        .split("T")[0]
        .split("-");
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    },
  },
  watch: {
    value(newVal) {
      if (newVal && this.request) {
        this.loadAffectedShifts();
      }
    },
  },
  methods: {
    formatRequestType(type) {
      const types = {
        single_full_day: "Single Full Day",
        single_partial_day: "Partial Day",
        multiple_full_days: "Multiple Days",
      };
      return types[type] || type;
    },

    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return "N/A";
      return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
    },

    formatTime(timeString) {
      if (!timeString) return "";
      // Convert 24-hour time to 12-hour format
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      // Parse date as local date to avoid timezone issues
      // Extract year, month, day from the date string (YYYY-MM-DD format)
      const [year, month, day] = dateString.split("T")[0].split("-");
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    capitalizeStatus(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    getStatusType(status) {
      const types = {
        pending: "warning",
        approved: "success",
        denied: "danger",
      };
      return types[status] || "info";
    },

    formatShiftDate(dateTimeString) {
      if (!dateTimeString) return "N/A";
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    async loadAffectedShifts() {
      if (!this.request) return;

      try {
        this.shiftsChecked = false;

        // Build query parameters
        const queryParams = {
          caregiver_id: this.request.id_users,
          start_date: this.request.start_date,
          end_date: this.request.end_date,
        };

        // For partial day requests, also pass the time range
        if (this.request.request_type === "single_partial_day") {
          if (this.request.start_time) {
            queryParams.start_time = this.request.start_time;
          }
          if (this.request.end_time) {
            queryParams.end_time = this.request.end_time;
          }
        }

        // Get shifts for this caregiver in the date range
        const response = await shiftsService.getByCaregiver(queryParams);

        this.affectedShifts = response.data || [];
        this.shiftsChecked = true;
      } catch (error) {
        this.affectedShifts = [];
        this.shiftsChecked = true;
      }
    },

    async handleApprove() {
      const shiftsCount = this.affectedShifts.length;
      const message =
        shiftsCount > 0
          ? `Approve this time off request? ${shiftsCount} shift${shiftsCount > 1 ? "s" : ""} will be automatically unassigned.`
          : "Approve this time off request?";

      const confirmed = await this.$confirmAction(
        () => this.reviewRequest("approved"),
        message,
      );

      if (!confirmed) return;
    },

    async handleDeny() {
      const confirmed = await this.$confirmAction(
        () => this.reviewRequest("denied"),
        "Are you sure you want to deny this time off request?",
      );

      if (!confirmed) return;
    },

    async reviewRequest(status) {
      if (this.isProcessing) return;

      this.isProcessing = true;

      try {
        const response = await timeOffRequestsService.reviewRequest(
          this.request.id,
          status,
          this.adminNotes || null,
        );

        const unassignedCount = response.data?.unassignedShiftsCount || 0;

        if (status === "approved") {
          this.$notifySuccess(
            `Time off approved successfully.${unassignedCount > 0 ? ` ${unassignedCount} shift${unassignedCount > 1 ? "s" : ""} unassigned.` : ""}`,
          );
        } else {
          this.$notifyInfo("Time off request denied");
        }

        this.$emit("reviewed", status);
        this.closeModal();
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Error processing request";
        this.$notifyError(errorMessage);
      } finally {
        this.isProcessing = false;
      }
    },

    closeModal() {
      this.isVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.adminNotes = "";
      this.affectedShifts = [];
      this.shiftsChecked = false;
      this.isProcessing = false;
    },
  },
};
</script>

<style scoped>
/* Review Details Content - Matching View Modal */
.review-details-content {
  padding: 0.5rem 0;
  max-height: calc(85vh - 180px);
  overflow-y: auto;
}

.detail-row {
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row strong {
  flex: 0 0 140px;
  color: #6ba6cd;
  font-weight: 600;
}

.detail-row span {
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
}

/* Affected Shifts Warning */
.affected-shifts-warning {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.no-shifts-info {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.alert {
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 0;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #ffc107;
}

.alert-info {
  background-color: rgba(23, 162, 184, 0.15);
  border: 1px solid rgba(23, 162, 184, 0.3);
  color: #17a2b8;
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.warning-header i {
  font-size: 14px;
}

.shifts-list {
  margin-top: 8px;
  font-size: 13px;
}

.shift-item {
  padding: 4px 0;
  color: rgba(255, 255, 255, 0.8);
}

.text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 12px;
  margin-top: 4px;
  font-style: italic;
}

/* Admin Notes Section */
.admin-notes-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.admin-notes-section label {
  display: block;
  margin-bottom: 8px;
  color: #6ba6cd;
}

.form-control {
  background-color: #2a2a2a;
  border: 1px solid #555;
  color: #d9d9d9;
  width: 100%;
  padding: 8px;
  border-radius: 4px;
}

.form-control:focus {
  background-color: #2a2a2a;
  border-color: #6ba6cd;
  color: #d9d9d9;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(107, 166, 205, 0.25);
}

.form-control::placeholder {
  color: #999;
}

/* Buttons */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
  border-color: #545b62;
}

/* Element UI Tag Styles */
::v-deep .el-tag {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}
</style>
